/**
 * Shared post-OAuth bootstrap + redirect for the public self-serve pricing
 * flow. Used by both AuthProvider (the automatic handler that runs on OAuth
 * return) and the PricingSetupError "Try again" button, so the success and
 * retry paths behave identically.
 *
 * Invokes the idempotent agency-public-signup-bootstrap edge function (with a
 * hard timeout so a hung call can never block the UI), then hard-navigates to
 * the right destination for the intent:
 *   free  -> the lead-gen tools dashboard
 *   paid  -> the agency checkout page, carrying plan / cycle / agency / source
 *
 * Throws on any failure (error response, missing agency_id, or timeout) so the
 * caller can surface the recoverable error screen. Marks the redirect flag
 * before the call so the app shell keeps its loading screen during the wait.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  markPricingRedirecting,
  type PendingPricingIntent,
} from './pricingIntent';
import { buildAgencyCheckoutPath, buildWelcomePath, TRIAL_DASHBOARD_URL, buildTrialWelcomePath } from './checkoutUrl';

// Cap the wait so a hung edge function can never trap the user. Kept under
// PRICING_REDIRECT_TTL_MS so the loader is released via this path (clean error
// screen), not the shell's failsafe timeout.
const BOOTSTRAP_TIMEOUT_MS = 15_000;

/** Error carrying the edge function's machine `code` so the recovery screen can
 *  map it to specific copy and the right recovery action. */
export class BootstrapError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'BootstrapError';
    this.code = code;
  }
}

// supabase-js hides a non-2xx function response in error.context (a Response);
// error.message is just the generic "non-2xx status code". Read the real body
// so we recover the function's { code, message }.
async function readBootstrapError(error: unknown, fallback: string): Promise<{ code?: string; message: string }> {
  try {
    const ctx = (error as { context?: Response } | null)?.context;
    if (ctx && typeof ctx.clone === 'function') {
      const raw = await ctx.clone().text().catch(() => '');
      if (raw) {
        try {
          const body = JSON.parse(raw) as Record<string, unknown>;
          const message =
            (typeof body.message === 'string' && body.message) ||
            (typeof body.error === 'string' && body.error) ||
            fallback;
          const code = typeof body.code === 'string' ? body.code : undefined;
          return { code, message };
        } catch {
          // non-JSON body — fall through
        }
      }
    }
  } catch {
    // ignore — fall back to the generic message below
  }
  return { message: (error as Error | null)?.message || fallback };
}

// Take intent.next (the funnel page the user came from, set by
// SignInButton) and return a URL that scrolls to the "pick a plan"
// section on that same page. If intent.next already carries a hash,
// respect it. If it's malformed / empty, fall back to
// /producthunt#pricing so we never send the user to a dead-end.
//
// Concrete examples:
//   '/'                          → '/pricing'         (SignInButton
//                                   already normalises home → '/pricing')
//   '/producthunt'               → '/producthunt#pricing'
//   '/producthunt?utm=xyz'       → '/producthunt?utm=xyz#pricing'
//   '/offer'                     → '/offer#pricing'
//   '/pricing'                   → '/pricing'          (page already IS
//                                   the plan surface; #pricing is redundant)
//   '/offer#pricing'             → '/offer#pricing'   (idempotent)
//   ''                           → '/producthunt#pricing' (fallback)
function buildFunnelPricingTarget(next: string): string {
  if (typeof next !== 'string' || !next.startsWith('/')) {
    return '/producthunt#pricing';
  }
  // If a hash is already present, honor it (idempotent).
  if (next.includes('#')) return next;
  // /pricing is itself the "pick a plan" surface — no anchor needed.
  const path = next.split('?')[0];
  if (path === '/pricing') return next;
  return next + '#pricing';
}

export async function bootstrapAndRedirectForIntent(
  intent: PendingPricingIntent,
): Promise<void> {
  markPricingRedirecting(Date.now());

  // Plain marketing-funnel lead-gen signup (e.g. /producthunt "Sign in" /
  // "Create account"). OAuth was routed through the whitelisted /fly-app
  // target precisely so the Supabase redirect allowlist can't bounce the
  // user onto the dashboard. Forward into the welcome step, which self-
  // bootstraps the lead_gen_only agency on mount and then returns the user
  // to `return_to` (the funnel page). Returning users whose profile is
  // already complete are bypassed straight through by the welcome page.
  // No bootstrap call here — the welcome page owns provisioning, so we
  // never double-provision and never flash the full Fly nav.
  if (intent.kind === 'lead_gen_signup') {
    window.location.replace(
      '/fly-app/welcome?return_to=' + encodeURIComponent(intent.return_to),
    );
    return;
  }

  // ClinicLeads signup. Bootstrap with the clinic product marker so the edge
  // function marks the agency clinic_only=true, grants the free scan tier, and
  // skips the lead-gen credit grant, then land the buyer in the clinic app
  // (NOT the audit dashboard). Bootstrap is idempotent + non-fatal: even if it
  // times out, the clinic app loads and entitlements reconcile on next read.
  if (intent.kind === 'clinic_signup') {
    try {
      await Promise.race([
        supabase.functions.invoke('agency-public-signup-bootstrap', {
          body: { intent: 'trial_later', product: 'clinic' },
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Setup timed out')), BOOTSTRAP_TIMEOUT_MS)),
      ]);
    } catch (e) {
      console.warn('[clinic-signup] bootstrap failed (non-fatal)', e);
    }
    // If a paid tier rode along, resume on the clinic checkout page for that
    // tier (matching the audit paid flow); otherwise land on the app home.
    window.location.replace(
      intent.plan ? '/fly-app/clinic?section=checkout&plan=' + intent.plan : '/fly-app/clinic',
    );
    return;
  }

  // Capture the bootstrap response so we can branch on `created` after the
  // race. Previously we only kept agency_id; now we also need the
  // freshly-provisioned signal that decides welcome vs straight-to-checkout.
  //
  // `intent` is the new opt-in-trial signal: 'free' grants the auto-trial,
  // 'paid' strips it server-side. Forwarded from the PendingPricingIntent
  // kind so /pricing "Try free for 7 days" still gets a trial while any
  // paid-plan CTA (including /producthunt) does not.
  const intentForBootstrap = intent.kind === 'free' ? 'free' : 'paid';
  const bootstrap = await Promise.race<{ agencyId: string; created: boolean }>([
    (async () => {
      const { data, error } = await supabase.functions.invoke(
        'agency-public-signup-bootstrap',
        // A paid plan names the buyer (P1): no "free trial" welcome while they pay.
        { body: intent.kind === 'paid' ? { intent: intentForBootstrap, plan: intent.plan } : { intent: intentForBootstrap } },
      );
      if (error || !data?.agency_id) {
        const detail = await readBootstrapError(error, 'Bootstrap returned no agency id');
        throw new BootstrapError(detail.message, detail.code);
      }
      return {
        agencyId: data.agency_id as string,
        // Defensive default: if the edge function ever omits `created`
        // (older deploy, schema drift), we treat it as an existing user
        // and use the current checkout path — never the welcome step.
        // That preserves the pre-welcome-step behaviour exactly.
        created: data.created === true,
      };
    })(),
    new Promise<{ agencyId: string; created: boolean }>((_, reject) =>
      setTimeout(() => reject(new Error('Setup timed out')), BOOTSTRAP_TIMEOUT_MS),
    ),
  ]);

  if (intent.kind === 'free') {
    // A brand-new account goes through the welcome step first, exactly like
    // every other funnel above. It was the only branch that ignored
    // `created` and jumped straight to the dashboard, which is why a fresh
    // trial signup landed on the tools page and was immediately covered by
    // the non-dismissible ProfileCompletionGate modal, with the product tour
    // auto-starting on top of that.
    //
    // The welcome step asks for the same display name and phone on a proper
    // full page, then sends them on to the dashboard itself, so by the time
    // the tools page loads the gate has nothing left to ask and the tour
    // opens over a usable screen.
    //
    // A returning user keeps the direct path: their profile is already
    // complete, and the welcome step would only bounce them straight
    // through (it short-circuits on a complete profile).
    window.location.replace(
      bootstrap.created
        ? buildTrialWelcomePath(bootstrap.agencyId)
        : TRIAL_DASHBOARD_URL,
    );
    return;
  }

  // Marketing-funnel signup (home "/", /producthunt, /pricing, or /ad
  // header CTAs). We ran the bootstrap with intent='paid' so no premature
  // auto-trial is granted and an existing customer's subscription is
  // never touched.
  //
  // Routing rules (per product spec):
  //   • Brand-new user (created) → welcome / profile step, then land back
  //     on the funnel page they came from with #pricing appended, so the
  //     "pick a plan" section scrolls into view. Each funnel keeps its
  //     visitor inside its own URL (/producthunt sign-up stays on
  //     /producthunt, /ad sign-up stays on /ad).
  //   • Returning user WITH an active plan or trial → /fly-app dashboard.
  //     LocalSEOApp routes lead-gen users onward to /lead-gen-tools;
  //     other users hit their own view.
  //   • Returning user WITHOUT an active plan → same funnel-page pricing
  //     anchor as new users. Catches expired trials, cancelled subs, and
  //     no-sub agencies so nobody dead-ends on the dashboard.
  //
  // Falling back to '/producthunt#pricing' if intent.next is somehow
  // malformed — safe default that always reaches a real plan surface.
  if (intent.kind === 'lead_gen_funnel_signup') {
    const funnelPricingTarget = buildFunnelPricingTarget(intent.next);
    if (bootstrap.created) {
      const qs = new URLSearchParams({
        agency: bootstrap.agencyId,
        return_to: funnelPricingTarget,
      });
      window.location.replace('/fly-app/welcome?' + qs.toString());
      return;
    }
    // Returning user — check plan status on their existing agency (which
    // bootstrap just idempotently returned). Mirrors useUserPlanState's
    // active/trial_active/lifetime classification so both surfaces treat
    // the same rows as "user has a live plan".
    try {
      const { data: subs } = await supabase
        .from('agency_subscriptions')
        .select('status, plan_slug, is_lifetime, cancelled_at')
        .eq('agency_id', bootstrap.agencyId)
        .order('created_at', { ascending: false })
        .limit(1);
      const sub = subs?.[0];
      const hasActivePlan =
        !!sub &&
        !sub.cancelled_at &&
        (sub.is_lifetime === true ||
          sub.status === 'trialing' ||
          (sub.status === 'active' &&
            ['starter', 'growth', 'scale'].includes(sub.plan_slug)));
      window.location.replace(hasActivePlan ? '/fly-app' : funnelPricingTarget);
    } catch (err) {
      // Sub lookup failed — safer to send them to the pricing page than
      // strand them on the dashboard with no plan hint.
      console.error('[bootstrapAndRedirectForIntent] plan lookup failed:', err);
      window.location.replace(funnelPricingTarget);
    }
    return;
  }

  // Paid path: brand-new agencies get a one-step profile-completion page
  // before the Razorpay checkout, so their first PDF report doesn't ship
  // with an auto-generated "X's Lead Gen Agency" title. Existing users
  // (created===false) keep the original direct-to-checkout flow.
  const target = bootstrap.created
    ? buildWelcomePath({
        plan: intent.plan,
        cycle: intent.cycle,
        agency: bootstrap.agencyId,
        source: intent.source,
        coupon: intent.coupon,
      })
    : buildAgencyCheckoutPath({
        plan: intent.plan,
        cycle: intent.cycle,
        agency: bootstrap.agencyId,
        source: intent.source,
        coupon: intent.coupon,
      });

  window.location.replace(target);
}
